import { Injectable } from '@angular/core';
import { ButtonModel, LookupModel, PbxAccountDeviceExpansionModel, PbxAccountModel } from '../../../../../api/models';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { speedDialPhoneNumberValidator } from '../../../../../core/validators/speed-dial-number.validator';
import { BUTTON_TYPES } from '../constants/device-button-types';
import { ButtonTypes } from '../enums/button-types.enum';

@Injectable({
  providedIn: 'root',
})
export class DeviceButtonSharedService {
  public lookupButtonParametersMap: Record<string, LookupModel[]>;

  private extension: PbxAccountModel = null;

  private get extensionWithNumber(): string {
    const { name, firstName, displayName } = this.extension || {};

    return `${name} ${firstName} ${displayName}`;
  }

  constructor(private readonly _fb: UntypedFormBuilder) {}

  public isParameterVisible(btnType: ButtonTypes): boolean {
    return (BUTTON_TYPES || []).find(btn => btn.value === btnType)?.parameter;
  }

  public getDefaultLabel(btnType: ButtonTypes, parameterId: string): string {
    switch (btnType) {
      case ButtonTypes.Speed:
        return '';
      case ButtonTypes.Private:
        return this.extensionWithNumber;

      default:
        const availableParameters = this.getParameterValues(btnType);

        return availableParameters?.find(param => param.id === parameterId)?.text || parameterId || '';
    }
  }

  public getParameterValues(btnType: ButtonTypes): LookupModel[] {
    return this.lookupButtonParametersMap[btnType];
  }

  public onButtonTypeChange(btnType: ButtonTypes, buttonGroup: UntypedFormGroup): void {
    const parameterControl = buttonGroup.get('parameter');
    const labelControl = buttonGroup.get('label');
    const controlsArray = [parameterControl, labelControl];

    controlsArray.forEach(control => {
      control?.setValue('');
    });

    parameterControl.setValidators(this.getParameterValidators(btnType));
    labelControl.setValidators(this.getLabelValidators(btnType));

    if (btnType === ButtonTypes.Private) {
      labelControl.setValue(this.extensionWithNumber);
    }

    controlsArray.forEach(control => {
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
  }

  public createExpansionGroup(expansion: PbxAccountDeviceExpansionModel): UntypedFormGroup {
    const { buttons, model } = expansion || {};

    const formGroup = this._fb.group({
      model: [model],
      buttons: this._fb.array((buttons || []).map(btn => this.createButtonGroup(btn))),
    });

    return formGroup;
  }

  public createButtonGroup(button: ButtonModel, template = false): UntypedFormGroup {
    const { fixed, group, identity, label, mode, name, number, parameter, pickup, sort, type } = button || {};
    const btnTypeFound = (BUTTON_TYPES || []).find(btn => btn.value === type);
    const btnType = btnTypeFound?.value || ButtonTypes.None;

    const btnDisabled = !!btnTypeFound?.disabled;

    const controls = {
      type: [{ value: btnType, disabled: btnDisabled }],
      parameter: [{ value: parameter || '', disabled: btnDisabled }, this.getParameterValidators(btnType, btnDisabled)],
      label: [{ value: label || '', disabled: btnDisabled }, this.getLabelValidators(btnType, btnDisabled)],

      ...(!template && {
        number: [number],
        name: [name],
        mode: [mode || '1'],
        sort: [sort],
        group: [group],
        identity: [identity || ''],
        pickup: [pickup || ''],
        fixed: [fixed || false],
      }),
    };

    const formGroup = this._fb.group(controls);

    if (btnDisabled) {
      formGroup.disable();
    }

    formGroup.markAllAsTouched();

    return formGroup;
  }

  public prepareData(accounts: LookupModel[], extension: PbxAccountModel): void {
    this.extension = extension;

    const accountMap: Record<string, string[]> = {
      [ButtonTypes.Park]: ['orbits'],
      [ButtonTypes.Extension]: ['extensions'],
      [ButtonTypes.Flag]: ['srvflags'],
      [ButtonTypes.Speed]: ['attendants', 'srvflags', 'hunts'],
      [ButtonTypes.Login]: ['acds'],
      [ButtonTypes.HuntGroup]: ['hunts'],
      [ButtonTypes.AgentGroup]: ['acds'],
    };

    this.lookupButtonParametersMap = Object.fromEntries(
      Object.entries(accountMap).map(([key, value]) => [
        key,
        (accounts || []).filter(account => value.includes(account.metadata)),
      ]),
    );
  }

  private getParameterValidators(btnType: ButtonTypes, isDisabled?: boolean): ValidatorFn[] {
    if (isDisabled) {
      return [];
    }

    if (!this.isParameterVisible(btnType)) {
      return [];
    }

    const validators = [Validators.required];

    if (btnType === ButtonTypes.Speed) {
      validators.push(speedDialPhoneNumberValidator);
    }

    return validators;
  }

  private getLabelValidators(btnType: ButtonTypes, isDisabled?: boolean): ValidatorFn[] {
    if (isDisabled) {
      return [];
    }

    return btnType === ButtonTypes.Speed ? [Validators.required] : [];
  }
}
